import React from 'react';

import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { BillableMetric } from 'src/features/billableMetrics/utils/types';
import { useDeleteBillableMetric } from 'src/features/billableMetrics/api/useDeleteBillableMetric';
import { LIST_BILLABLE_METRICS_RESOURCE } from 'src/features/billableMetrics/api/useListBillableMetrics';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';

interface DeleteBillableMetricFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillableMetric: BillableMetric;
    children?: React.ReactNode;
}

function DeleteBillableMetricFormModal<TData>({
    selectedBillableMetric,
    children,
}: DeleteBillableMetricFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deleteBillableMetric } = useDeleteBillableMetric({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLABLE_METRICS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(billableMetricsMessages.deleteBillableMetric),
                description: formatMessage(billableMetricsMessages.deleteBillableMetricSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...billableMetricsMessages.deleteBillableMetric },
                subTitle: {
                    ...billableMetricsMessages.deleteBillableMetricSubtitle,
                    values: {
                        entity: selectedBillableMetric.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...billableMetricsMessages.confirmDeleteBillableMetric },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: billableMetricsMessages.deleteBillableMetric,
                callbackFn: () => {
                    return (
                        deleteBillableMetric({
                            metricId: selectedBillableMetric.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteBillableMetricFormModal;
